import { useCallback } from 'react';

import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { PlanUnitMix } from 'components/types';

import { View } from '../../enums';

import { GRAY } from 'constants/colors';

import { mutations } from 'RestAPIs/rapini';

import { planUnitsAtom } from '../../atoms';

import {
  Box,
  Button,
  FormControlLabel,
  Skeleton,
  styled,
  Switch
} from '@mui/material';

import {
  DateNavigation,
  LoadingOverlay,
  PlanUnitSelector,
  showSnackbar
} from 'components/shared';

import useCopyMode from '../../hooks/useCopyMode';
import useSelectedDate from '../../hooks/useSelectedDate';
import useSelectedDateRange from '../../hooks/useSelectedDateRange';
import ActionButton from './ActionButton';
import EntityMenu from './EntityMenu';
import ViewSwitch from './ViewSwitch';
import ZoomLevelMenu, { ZoomLevel } from './ZoomLevelMenu';
import { getPlanUnitIds } from '../../../../utils';
import { setUserPreference } from '../../../../../redux/actionCreators';
import { Period } from 'components/shared/DateNavigation/DateNavigation';
import DaypartFilter from '../DaypartFilter/DaypartFilter';

const StyledHeader = styled(Box)`
  z-index: 40;
  width: 100%;
  background: white;
`;

const Top = styled(Box)`
  position: relative;
  padding: 16px;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(475px, auto));
  border-bottom: 1px solid ${GRAY};

  .MuiAutocomplete-root {
    min-width: 200px;
  }
`;

interface Props {
  view: View;
  handleSelectPlanUnit: (options: PlanUnitMix[]) => void;
  handleChangeView: (view: View) => void;
  isEmployeeRole: boolean;
}

export default function Header({
  handleSelectPlanUnit,
  handleChangeView,
  view,
  isEmployeeRole
}: Props) {
  const { t } = useTranslation('common');
  const {
    hasActivityPlanningEditAccess,
    hasWorkPlacePlanningEditAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess
  } = useSelector(({ grants }) => grants);
  const dispatch = useDispatch();
  const selectedPlanunits = useRecoilValue(planUnitsAtom);
  const { activityPlanningOnlyPlanned } = useSelector(
    (state) => state.preferences
  );
  const [selectedDate, setSelectedDate] = useSelectedDate();
  const { startDate, endDate } = useSelectedDateRange();
  const { isCopyMode, endCopyMode } = useCopyMode();

  const saveOnlyPlanned = (value: boolean) => {
    dispatch(
      setUserPreference({
        key: 'activityPlanningOnlyPlanned',
        value
      })
    );
  };

  const { mutate, isLoading } =
    mutations.useSyncAppointmentGroupingsWithDuties();

  const handleSyncClick = useCallback(() => {
    mutate(
      {
        startDate: startDate as string,
        endDate: endDate as string,
        organizationalUnitIds: getPlanUnitIds(selectedPlanunits)
      },
      {
        onSuccess: () => {
          showSnackbar({
            severity: 'success',
            message: t('activityPlanning.syncDutiesSuccess')
          });
        },
        onError: () => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        }
      }
    );
  }, [startDate, endDate, selectedPlanunits]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveOnlyPlanned(event.target.checked);
  };

  const getDefaultPeriodByView = (view: View): Period => {
    switch (view) {
      case View.List:
        return 'week';
      case View.Roster:
        return 'week';
      case View.Timeline:
        return 'week';
      default:
        return 'day';
    }
  };

  const getPeriodsByView = (view: View): Period[] => {
    switch (view) {
      case View.Timeline:
        return ['day', 'week', 'workweek'];
      case View.Roster:
        return ['week', 'workweek'];
      default:
        return ['day', 'week', 'month'];
    }
  };

  return (
    <>
      <StyledHeader component="header">
        <Top>
          <Box display="flex">
            <PlanUnitSelector
              useEmployeePlanunits={isEmployeeRole}
              onSelected={handleSelectPlanUnit}
              disabled={isCopyMode ? true : undefined}
            />
            {view === View.Roster && (
              <Box
                sx={{
                  position: 'relative',
                  top: '5px',
                  paddingLeft: '10px'
                }}
              >
                <FormControlLabel
                  label={t(`planned`)}
                  control={
                    <Switch
                      checked={activityPlanningOnlyPlanned}
                      onChange={handleChange}
                    />
                  }
                />
              </Box>
            )}
          </Box>
          <Box>
            {view ? (
              <DateNavigation
                defaultPeriod={getDefaultPeriodByView(view)}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                disabled={isCopyMode}
                allowMenuOptions={
                  view === View.Timeline || view === View.Roster
                }
                options={getPeriodsByView(view)}
              />
            ) : (
              <Skeleton />
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
            sx={{ gridColumnEnd: -1 }}
          >
            {(view === View.Cards || view === View.Timeline) && (
              <DaypartFilter isCompact={true} />
            )}
            {(view === View.Roster ||
              view === View.Cards ||
              view === View.Timeline) && (
              <ZoomLevelMenu
                disabled={isCopyMode}
                disableZoomLevels={
                  view === View.Timeline ? [ZoomLevel.DETAILED] : null
                }
                minimized={view === View.Roster || view === View.Timeline}
              />
            )}
            {(view === View.Roster ||
              view === View.List ||
              view === View.Timeline) && (
              <EntityMenu view={view} disabled={isCopyMode} />
            )}
            {view && (
              <ViewSwitch
                view={view}
                handleChangeView={handleChangeView}
                disabled={isCopyMode}
              />
            )}
            {(hasActivityPlanningEditAccess ||
              hasWorkPlacePlanningEditAccess ||
              hasMyActivitiesProjectAccess ||
              hasManageOwnProjectsAccess ||
              hasProjectTeamAccess) && (
              <ActionButton
                view={view}
                handleSyncClick={handleSyncClick}
                disabled={isCopyMode}
              />
            )}
            {isCopyMode && (
              <Button
                variant="contained"
                sx={{
                  px: '14px',
                  height: '40px'
                }}
                color="primary"
                onClick={() => endCopyMode()}
              >
                Klaar
              </Button>
            )}
          </Box>
        </Top>
      </StyledHeader>
      <LoadingOverlay loading={isLoading} />
    </>
  );
}
